import React from "react";
import { productsDelete } from "../../../api/api";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { generateClassName } from "../../../hooks/useAttributes";
import useRoutes from "../../../hooks/useRoutes";
import { useQueryValue } from "../../../hooks/useSearch";
import { useProducts } from "../../../state/api/products/useProducts";
import { useSession } from "../../../state/api/session/useSession";
import { setProductExpanded } from "../../../state/slices/uiSlice";
import { Doc } from "../../../types/db/collections";
import { FILE_TYPE, GetFileResponse } from "../../../types/db/schema/files/file.schema";
import { GetProductDto } from "../../../types/db/schema/products/product.schema";
import { QUERY_KEY } from "../../../types/query.types";
import { AccordionPage, AccordionPageTitle } from "../accordion/AccordionPage";
import Button from "../buttons/Button";
import DeleteButton from "../buttons/DeleteButton";
import FileDialog from "../files/FileDialog";
import FileItem from "../files/FileItem";
import Icon from "../icons/Icon";
import PageLoader from "../loader/PageLoader";
import Pill from "../pill/Pill";
import AsAdmin from "../session/AsAdmin";
import Tabs, { Tab, TabProps } from "../tabs/Tabs";
import ProductDialog from "./ProductDialog";
import "./ProductsList.css";

type ProductTabProps = TabProps & {
  color: string;
  product?: Doc<GetProductDto>;
  label?: string;
}

function ProductPageTab(props: ProductTabProps) {
  const { product, label, color } = props;

  const extendedLabel = product?.name;

  return (
    <Tab {...props}>
      <div className="product-tab-list-color-indicator" style={{ backgroundColor: color }} />
      <div className={generateClassName({
        value: !!extendedLabel,
        onTrue: "d-flex d-sm-none"
      })}>
        {product?.shortcode || label}
      </div>
      {
        extendedLabel && (
          <div className="d-none d-sm-flex">
            {extendedLabel}
          </div>
        )
      }
      {
        (product && !product.hasAccess) && (
          <Icon
            icon="lock-fill"
            size={10}
            color="#e8943a"
          />
        )
      }
    </Tab>
  )

}

interface WithProductProps {
  product: Doc<GetProductDto>;
}

function ProductUnlockPrompt(props: WithProductProps) {
  const { product } = props;

  const { openInBrowser } = useRoutes();

  if (product.hasAccess) {
    return null;
  }

  return (
    <div className="w-100 d-flex flex-column gap-2 ps-3 pe-3">
      <div className="product-unlock-prompt d-flex flex-row align-items-center justify-content-between gap-3 p-3">
        <div className="d-flex flex-row align-items-center gap-2">
          <div className="product-unlock-lightning">
            <span>⚡</span>
          </div>
          <div className="d-flex flex-column product-unlock-content me-1">
            <strong>Unlock {product.name}</strong>
            <span>{product.description ?? "Unlock full access to this product"}</span>
          </div>
        </div>
        <Button
          onClick={() => openInBrowser(product.unlockLink || "https://community.mfaunderwriting.com")}
          text="Upgrade"
          color="var(--teal-600)"
          icon="arrow-right-short"
        />
      </div>
    </div>
  )
}

interface ProductFileProps extends WithProductProps {
  files: Doc<GetFileResponse>[];
}

function ProductFiles(props: ProductFileProps) {
  const { files, product } = props;

  return (
    <div className="d-flex flex-column gap-3 ps-1 pe-1">
      {
        files?.length
          ? files.map(f => (
            <FileItem
              key={f._id}
              hasAccess={product.hasAccess}
              file={f}
            />
          ))
          : <span className="no-files font-s">No files</span>
      }
    </div>
  );
}

function ProductOverviewUi(props: WithProductProps) {
  const { product } = props;

  const { value } = useQueryValue(QUERY_KEY.PRODUCT_Q);

  const dispatch = useAppDispatch();
  const { expandedProducts } = useAppSelector(state => state.ui);

  const { mutate, isRefetching } = useProducts({ enabled: false });
  const { session } = useSession();

  if (!product?.files?.length && !session?.isAdmin) {
    return null;
  }

  const isSearching = !!value;

  return (
    <AccordionPage
      background="var(--teal-100)"
      hover="var(--gray-50)"
      open={(isSearching && !isRefetching) || !!expandedProducts[product._id]}
      onToggle={o => dispatch(setProductExpanded({ id: product._id, expanded: o }))}
      actionItems={(
        session.isAdmin && (
          <div className="d-flex flex-row align-items-center">
            <FileDialog
              file={{ product: product._id }}
              size="small"
              color="var(--gray-600)"
              variant="ghost"
              text=""
            />
            <ProductDialog
              product={product}
              color="var(--gray-600)"
              size="small"
              text=""
              variant="ghost"
            />
            <DeleteButton
              text=""
              size="small"
              variant="ghost"
              apiCall={async () => await productsDelete(product._id)}
              afterDelete={mutate}
            />
          </div>
        )
      )}
      title={(
        <div className="w-100 d-flex flex-row justify-content-between align-items-center pe-1">
          <div className="d-flex flex-row align-items-center gap-2 pt-2 pb-2">
            <Pill background={product.tabColor}>
              {product.shortcode}
            </Pill>
            <AccordionPageTitle>
              {product.name}
            </AccordionPageTitle>
            {
              (!product.isActive) && (
                <Pill background="var(--gray-200)" foreground="var(--gray-600)" >
                  Inactive
                </Pill>
              )
            }
          </div>
          {
            product.hasAccess
              ? (
                <Pill
                  round
                  background="var(--gray-200)"
                  foreground="var(--gray-600)"
                >
                  {product.files?.length ?? 0}
                </Pill>

              )
              : (
                <Pill background="#fef3c7"
                  foreground="#92400e"
                  round
                  border="1px solid #fde68a">
                  <Icon icon="lock-fill" color="#92400e" size={10} />
                </Pill>
              )
          }
        </div>
      )}>
      <ProductFiles product={product} files={product.files} />
      <ProductUnlockPrompt product={product} />
    </AccordionPage>
  )
}

interface FileGroup {
  type: FILE_TYPE;
  label: string;
}

function SingleProductUi(props: WithProductProps & { isConsolidated?: boolean }) {

  const { product, isConsolidated } = props;

  const { mutate } = useProducts({ enabled: false });
  const { session } = useSession();

  const groups: FileGroup[] = [
    {
      type: FILE_TYPE.MODEL,
      label: "Underwriting Models",
    },
    {
      type: FILE_TYPE.TOOL,
      label: product?.requiredTags?.length ? "Supporting Tools" : "Free Tools",
    }
  ]

  const actions = (
    <AsAdmin>
      <div className="d-flex flex-column gap-2 ps-3 pe-3 pt-2">
        <strong className="text-uppercase font-s" style={{ color: "var(--gray-600)" }}>
          Product Actions
        </strong>
        <div className="d-flex flex-row align-items-center gap-2">
          <FileDialog file={{ product: product._id }} />
          <div style={{ height: "24px", width: "1px", backgroundColor: "var(--gray-100)" }} />
          <ProductDialog product={product} />
          <DeleteButton
            variant="subtle"
            text="Delete"
            apiCall={async () => await productsDelete(product._id)}
            afterDelete={mutate}
          />
        </div>
      </div>
    </AsAdmin>
  );

  const fileGroup = (f: FileGroup) => {

    const { type, label } = f;

    const files = product.filesMap[type] ?? [];

    if (!files?.length && !session?.isAdmin) {
      return null;
    }

    return (
      <AccordionPage
        background="var(--teal-100)"
        hover="var(--gray-50)"
        key={type}
        open
        actionItems={(
          <FileDialog
            file={{ product: product._id, type }}
            size="small"
            color="var(--gray-600)"
            variant="ghost"
            text=""
          />
        )}
        title={(
          <div className="w-100 d-flex flex-row justify-content-between align-items-center pe-1">
            <div className="d-flex flex-row align-items-center gap-2">
              <AccordionPageTitle>
                {f.label}
              </AccordionPageTitle>
              {
                (!product.isActive) && (
                  <Pill background="var(--gray-100)" foreground="var(--gray-600)" >
                    Inactive
                  </Pill>
                )
              }
            </div>
            <Pill
              round
              background="var(--gray-100)"
              foreground="var(--gray-600)"
            >
              {files?.length ?? 0}
            </Pill>
          </div>
        )}>
        <ProductFiles product={product} files={files} />
      </AccordionPage>
    )
  }

  return (
    <div className="w-100 d-flex flex-column gap-3">
      {
        groups.map(g => fileGroup(g))
      }
      <div className="w-100 d-flex flex-column gap-2">
        {
          actions
        }
        <ProductUnlockPrompt product={product} />
      </div>
    </div>
  )
}


export default function ProductsList() {

  const [displayed, setDisplayed] = React.useState<number>(-1);

  const {
    isLoading,
    products,
    mutate
  } = useProducts();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!products?.length) {
    return (
      <div className="p-3">
        <span>No Products available</span>
      </div>
    )
  }

  const product = displayed >= 0 && products[displayed] ? products[displayed] : null;

  return (
    <div className="d-flex flex-column" style={{ flexGrow: 1, minHeight: 0 }}>
      <Tabs onClick={setDisplayed} active={displayed}>
        <ProductPageTab value={-1} label="All" color="#000" />
        {
          products.map((p, k) => (
            <ProductPageTab
              key={p._id}
              value={k}
              product={p}
              color={p.tabColor || "#000"}
            />
          ))

        }
      </Tabs>
      <div className="d-flex flex-column p-sm-4 p-3 gap-3" style={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }} >
        {
          product
            ? <SingleProductUi product={product} />
            : products.map(p => <ProductOverviewUi key={p._id} product={p} />)
        }
        <Button
          className="w-100"
          text="Refresh List"
          variant="ghost"
          size="large"
          color="var(--gray-400)"
          icon="arrow-clockwise"
          onClick={async () => await mutate()}
        />
      </div>
    </div>
  )
}